// /finance/cashflow — 现金流预测
// v1.31: 30 / 60 / 90 天 forecast + 每日明细
// 公式: expectedInflow = sum(AR.outstanding for order where etaDate in [today, today+N])
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { CardSkeleton } from '../components/Skeleton';
import { financeApi } from '../lib/api';
import { fmtDate, fmtMoney, fmtMoneyCompact } from '../lib/format';

export const Route = createFileRoute('/finance/cashflow')({
  component: CashflowPage,
});

// v1.31: 导出 for unit test
export { CashflowPage };

const HORIZONS = [30, 60, 90] as const;

function CashflowPage() {
  const [days, setDays] = useState<30 | 60 | 90>(30);
  const q = useQuery({
    queryKey: ['finance', 'cashflow', days],
    queryFn: () => financeApi.cashflow(days),
    refetchInterval: 120000,
  });

  const data = q.data;
  const currency = data?.currency ?? 'USD';

  // Daily breakdown stats
  const daily = data?.dailyBreakdown ?? [];
  const maxInflow = Math.max(...daily.map((d) => d.inflowCents), 1);
  const totalOrders = daily.reduce((acc, d) => acc + d.orderCount, 0);

  // 找哪天有 in
  const hasInflowDays = daily.filter((d) => d.inflowCents > 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="text-xs text-ink-3">
          起点 {data ? fmtDate(new Date(data.startDate).getTime()) : '—'} · 终点{' '}
          {data ? fmtDate(new Date(data.endDate).getTime()) : '—'}
        </div>
        <div className="inline-flex border border-line rounded overflow-hidden">
          {HORIZONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs border-l border-line first:border-l-0 ${
                days === d ? 'bg-accent text-white' : 'bg-surface text-ink-2 hover:bg-surface-2'
              }`}
            >
              {d} 天
            </button>
          ))}
        </div>
      </div>

      {/* Total cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <BigCard
          label="预期入账"
          value={fmtMoneyCompact(data?.expectedInflowCents ?? 0, currency)}
          sub={`未来 ${days} 天总计`}
          loading={q.isLoading}
          tone="good"
        />
        <BigCard
          label="预期出账"
          value={fmtMoneyCompact(data?.expectedOutflowCents ?? 0, currency)}
          sub="v1.19 接 supplier 表后实装"
          loading={q.isLoading}
          tone="muted"
        />
        <BigCard
          label="净现金流"
          value={fmtMoneyCompact(data?.netCashflowCents ?? 0, currency)}
          sub={`入账 - 出账 · ${totalOrders} 订单到期`}
          loading={q.isLoading}
          tone="accent"
        />
      </div>

      {/* Daily chart */}
      <div className="bg-surface border border-line rounded mb-5">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 className="text-sm font-semibold">每日现金流 (条形图)</h2>
          <button
            type="button"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1"
          >
            <Icon name="refresh-cw" size="xs" className={q.isFetching ? 'animate-spin' : ''} />
            {q.isFetching ? '刷新中' : '刷新'}
          </button>
        </div>
        {q.isLoading ? (
          <div className="p-4">
            <CardSkeleton />
          </div>
        ) : hasInflowDays.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon="trending-up"
              title="未来 N 天无应收到期"
              description="检查订单 ETA / 客户付款条款, 或扩大时间范围"
            />
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-end gap-px h-32 overflow-x-auto">
              {daily.map((d) => {
                const heightPct = maxInflow > 0 ? (d.inflowCents / maxInflow) * 100 : 0;
                const hasIn = d.inflowCents > 0;
                return (
                  <div
                    key={d.date}
                    className="flex-1 min-w-[2px] flex flex-col items-center justify-end group relative"
                    title={`${d.date}: ${fmtMoney(d.inflowCents, currency)} (${d.orderCount} 单)`}
                  >
                    {hasIn && (
                      <div
                        className="w-full bg-accent/70 hover:bg-accent transition-colors min-h-[1px]"
                        style={{ height: `${heightPct}%` }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-ink-3 mt-1">
              <span>{data?.startDate}</span>
              <span>{data?.endDate}</span>
            </div>
          </div>
        )}
      </div>

      {/* Daily details — only show days with inflow */}
      <div className="bg-surface border border-line rounded mb-5">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="text-sm font-semibold">有入账的日期</h2>
        </div>
        {q.isLoading ? (
          <div className="p-4">
            <CardSkeleton />
          </div>
        ) : hasInflowDays.length === 0 ? (
          <div className="p-4 text-center text-ink-3 text-xs">—</div>
        ) : (
          <div className="divide-y divide-line">
            {hasInflowDays.slice(0, 30).map((d) => (
              <div key={d.date} className="px-4 py-2.5 flex items-center gap-3 hover:bg-surface-2">
                <div className="w-24 text-xs font-mono text-ink-2">{d.date}</div>
                <div className="flex-1 text-xs text-ink-3">{d.orderCount} 订单到期</div>
                <div className="text-sm font-semibold text-good">
                  {fmtMoney(d.inflowCents, currency)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      {data?.notes && data.notes.length > 0 && (
        <div className="bg-surface-2 rounded p-3 text-[10px] text-ink-3 space-y-0.5">
          {data.notes.map((n) => (
            <p key={n}>· {n}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function BigCard({
  label,
  value,
  sub,
  loading,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub: string;
  loading: boolean;
  tone?: 'default' | 'good' | 'accent' | 'muted';
}) {
  const valueClass =
    tone === 'good'
      ? 'text-good'
      : tone === 'accent'
        ? 'text-accent'
        : tone === 'muted'
          ? 'text-ink-3'
          : '';
  return (
    <div className="bg-surface border border-line rounded p-4">
      <div className="text-xs text-ink-3 mb-1">{label}</div>
      {loading ? (
        <div className="h-7 w-32 bg-surface-2 rounded animate-pulse" />
      ) : (
        <div className={`text-xl sm:text-2xl font-semibold ${valueClass}`}>{value}</div>
      )}
      <div className="text-[10px] text-ink-3 mt-1">{sub}</div>
    </div>
  );
}
